"use client";

import { c as _c } from "react/compiler-runtime";
import { PixelArrow } from "@/components/ui/pixel-arrow";
import { cn } from "@/lib/utils";
import type { Template } from "@/stores/templatesStore";
import { useTemplatesStore } from "@/stores/templatesStore";
import { EASE_OUT_QUART, MOTION_DURATION } from "@/lib/animation-constants";
import { getRecommendedTemplateSlugsForGoalIds, readOnboardingGoalSelection } from "@/lib/onboardingGoalSelection";
import { motion, useInView, useReducedMotion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FeaturedTemplateCard } from "./FeaturedTemplateCard";
import { FeaturedTemplatesCarouselSkeleton } from "./FeaturedTemplatesCarouselSkeleton";
const CARD_STEP_PX = 320;
const EDGE_PADDING_PX = 32;
const DEFAULT_ENTRANCE_DELAY_SEC = 0;
export function FeaturedTemplatesCarousel({
  animateWhen = true,
  entranceDelay = DEFAULT_ENTRANCE_DELAY_SEC
}: {
  animateWhen?: boolean;
  entranceDelay?: number;
}) {
  const templates = useTemplatesStore(state => state.templates);
  const loading = useTemplatesStore(state_0 => state_0.loading);
  const initialized = useTemplatesStore(state_1 => state_1._initialized);
  const ensureInitialized = useTemplatesStore(state_2 => state_2._ensureInitialized);
  const [onboardingTemplateSlugs, setOnboardingTemplateSlugs] = useState<string[]>([]);
  useEffect(() => {
    ensureInitialized();
  }, [ensureInitialized]);
  useEffect(() => {
    const syncOnboardingGoals = () => {
      const selection = readOnboardingGoalSelection();
      setOnboardingTemplateSlugs(selection ? getRecommendedTemplateSlugsForGoalIds(selection.goalIds) : []);
    };
    syncOnboardingGoals();
    window.addEventListener("storage", syncOnboardingGoals);
    return () => window.removeEventListener("storage", syncOnboardingGoals);
  }, []);
  const promoted = useMemo(() => templates.filter(t => t.isPromoted).sort((a, b) => (a.promotedOrder ?? 0) - (b.promotedOrder ?? 0)), [templates]);
  const {
    displayTemplates,
    personalized
  } = useMemo(() => {
    const templateBySlug = new Map<string, Template>();
    templates.forEach(template => {
      templateBySlug.set(template.id, template);
      if (template.slug) templateBySlug.set(template.slug, template);
    });
    const onboardingTemplates = onboardingTemplateSlugs.map(slug => templateBySlug.get(slug)).filter((template_0): template_0 is Template => Boolean(template_0));
    if (onboardingTemplates.length === 0) return {
      displayTemplates: promoted,
      personalized: false
    };
    const seen = new Set<string>();
    const merged = [...onboardingTemplates, ...promoted].filter(template_1 => {
      if (seen.has(template_1.id)) return false;
      seen.add(template_1.id);
      return true;
    });
    return {
      displayTemplates: merged,
      personalized: true
    };
  }, [onboardingTemplateSlugs, promoted, templates]);
  if (templates.length === 0 && (!initialized || loading)) return <FeaturedTemplatesCarouselSkeleton />;
  if (displayTemplates.length === 0) return null;
  return <FeaturedTemplatesCarouselInner title={personalized ? "Recommended for you" : "Featured on B3OS"} templates={displayTemplates} animateWhen={animateWhen} entranceDelay={entranceDelay} />;
}
function FeaturedTemplatesCarouselInner(t0) {
  const $ = _c(35);
  const {
    title,
    templates,
    animateWhen,
    entranceDelay
  } = t0;
  const scrollerRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = direction => {
      const sign = direction === "prev" ? -1 : 1;
      scrollerRef.current?.scrollBy({
        left: sign * CARD_STEP_PX,
        behavior: "smooth"
      });
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleScroll = t1;
  const sectionRef = useRef(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      once: true,
      amount: 0.2
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isInView = useInView(sectionRef, t2);
  const isReadyToEnter = shouldReduceMotion || animateWhen && isInView;
  let t3;
  if ($[2] !== shouldReduceMotion) {
    t3 = shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 8
    };
    $[2] = shouldReduceMotion;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const hiddenState = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      opacity: 1,
      y: 0
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const visibleState = t4;
  const t5 = shouldReduceMotion ? false : hiddenState;
  const t6 = isReadyToEnter ? visibleState : hiddenState;
  let t7;
  if ($[5] !== entranceDelay || $[6] !== shouldReduceMotion) {
    t7 = shouldReduceMotion ? {
      duration: 0
    } : {
      duration: MOTION_DURATION.normal,
      delay: entranceDelay,
      ease: EASE_OUT_QUART
    };
    $[5] = entranceDelay;
    $[6] = shouldReduceMotion;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== title) {
    t8 = <h2 className="text-ds-text-primary leading-30 text-xl font-medium">{title}</h2>;
    $[8] = title;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== templates.length) {
    t9 = templates.length > 1 && <div className="flex items-start pr-px">{([{
        dir: "prev",
        label: "Previous",
        arrow: "left"
      }, {
        dir: "next",
        label: "Next",
        arrow: "right"
      }] as const).map(btn => <button key={btn.dir} type="button" aria-label={btn.label} onClick={() => handleScroll(btn.dir)} className={cn("hover:bg-ds-surface-muted border-ds-border-default bg-ds-surface-card text-ds-text-secondary group -mr-px flex size-9 cursor-pointer items-center justify-center overflow-hidden border p-[3px] transition-colors")}><PixelArrow direction={btn.arrow} /></button>)}</div>;
    $[10] = templates.length;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t5 || $[13] !== t6 || $[14] !== t7 || $[15] !== t8 || $[16] !== t9) {
    t10 = <motion.div initial={t5} animate={t6} transition={t7} className="mb-4 flex items-center justify-between gap-4 px-8">{t8}{t9}</motion.div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      paddingInline: `${EDGE_PADDING_PX}px`,
      scrollPaddingInline: `${EDGE_PADDING_PX}px`
    };
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== entranceDelay || $[20] !== hiddenState || $[21] !== isReadyToEnter || $[22] !== shouldReduceMotion || $[23] !== templates) {
    let t13;
    if ($[25] !== entranceDelay || $[26] !== hiddenState || $[27] !== isReadyToEnter || $[28] !== shouldReduceMotion) {
      t13 = (template, idx) => <motion.div key={template.id} initial={shouldReduceMotion ? false : hiddenState} animate={isReadyToEnter ? visibleState : hiddenState} transition={shouldReduceMotion ? {
        duration: 0
      } : {
        duration: MOTION_DURATION.normal,
        delay: entranceDelay + 0.08 + idx * 0.045,
        ease: EASE_OUT_QUART
      }} className="snap-start"><FeaturedTemplateCard template={template} /></motion.div>;
      $[25] = entranceDelay;
      $[26] = hiddenState;
      $[27] = isReadyToEnter;
      $[28] = shouldReduceMotion;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    t12 = templates.map(t13);
    $[19] = entranceDelay;
    $[20] = hiddenState;
    $[21] = isReadyToEnter;
    $[22] = shouldReduceMotion;
    $[23] = templates;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[30] !== t12) {
    t13 = <div ref={scrollerRef} className="scrollbar-hide flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth pb-2 pt-1" style={t11}>{t12}</div>;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t10 || $[33] !== t13) {
    t14 = <section ref={sectionRef} className="pb-10 pt-4">{t10}{t13}</section>;
    $[32] = t10;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
}